import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Rx';
import { NotificationsService } from 'angular2-notifications';
import { CapitalizePipe } from 'app/shared/capitalize.pipe';
import { DadosInscricao } from './DadosInscricao';
import { CpfCnpjPipe } from 'app/shared/cpf-cnpj.pipe';

@Injectable()
export class FluxoInscricaoService {

  cpfCnpjPipe = new CpfCnpjPipe();

  passoAtual = 1;
  dadosInscricao: DadosInscricao = new DadosInscricao({});
  private resumoFluxo: any[];

  // Observable string sources
  private dadosInscricaoMessage = new Subject<any>();
  private resumoFluxoMessage = new Subject<any[]>();

  // Observable string streams
  dadosInscricaoMessage$ = this.dadosInscricaoMessage.asObservable();
  resumoFluxoMessage$ = this.resumoFluxoMessage.asObservable();

  private capitalizePipe = new CapitalizePipe();

  confCapitalize = {
    todasPalavras: true,
    forcarCaixaBaixa: ['em', 'da', 'de', 'di', 'do', 'du', 'dos', 'das', 'e', 'a', 'ao'],
  };


  constructor(
    private notificationsService: NotificationsService,
  ) {
  }

  getPassoAtual(): number {
    return this.passoAtual;
  }

  private setPassoAtual(passoAtual: number): void {
    this.passoAtual = passoAtual;
  }

  getDadosInscricao(): DadosInscricao {
    return this.dadosInscricao;
  }

  setDadosInscricao(payload: any): void {
    this.dadosInscricao = { ...this.dadosInscricao, ...payload };
    this.dadosInscricaoMessage.next(this.dadosInscricao);
    this.atualizaResumoFluxo();
  }

  proximoPasso(payload?: any): void {
    this.setPassoAtual(this.passoAtual + 1);
    if (payload) {
      this.setDadosInscricao(payload);
    }
  }

  voltarPasso(): void {
    if (this.passoAtualRetornavel()) {
      this.setPassoAtual(this.passoAtual - 1 <= 0 ? 1 : this.passoAtual - 1);
      this.atualizaResumoFluxo();
    }
  }

  passoAtualRetornavel(): boolean {
    const fluxo = this.getResumoFluxo();
    if (fluxo && fluxo.length > 0 && this.passoAtual > fluxo[0].id) {
      // Verifica se existe algum passo anterior que seja final
      const passosFinais = fluxo.filter(p => p.id < this.passoAtual && p.final);
      return passosFinais.length === 0 && this.podeAlterarDadosInscricao();
    }
    return false;
  }

  podeAlterarDadosInscricao() {
    return !(this.dadosInscricao && this.dadosInscricao.agendamento && this.dadosInscricao.agendamento.numeroInscricao);
  }

  atualizarPassoPorRota(rotaAtual: string) {
    const passo = this.getResumoFluxo().filter(passoFluxo => passoFluxo.rota === rotaAtual);
    if (passo.length > 0) {
      this.setPassoAtual(passo[0].id);
      this.atualizaResumoFluxo();
    } else {
      this.notificationsService.error('Ação não permitida!', 'Para completar a inscrição você precisa passar por cada etapa na ordem estabelecida.');
    }
  }

  proximaRota(): string {
    // usar sempre depois de proximoPasso, pois depende do incremento da var passoAtual
    const proximoPasso = this.getResumoFluxo().filter(passo => passo.id === this.passoAtual);
    if (proximoPasso.length <= 0) {
      return null;
    }
    return proximoPasso[0].rota;
  }

  rotaAnterior(): string {
    const passoAnterior = this.getResumoFluxo().filter(passo => passo.id === this.passoAtual);
    if (passoAnterior.length <= 0) {
      return null;
    }
    return passoAnterior[0].rota;
  }

  validarRotaAtual(rota: string): boolean {
    const rotaValida = this.getResumoFluxo()
      .filter(passo => passo.id === this.getPassoAtual() && passo.rota === rota).length > 0;
    return rotaValida;
  }

  getResumoFluxo(): any[] {
    if (!this.resumoFluxo || this.resumoFluxo.length === 0) {

      // escolhe qual fluxo e o correto ( COM FORMA DE PAGAMENTO OU NAO)
      if (this.dadosInscricao.turma && this.dadosInscricao.turma.possuiInscricao === false) {
        this.resumoFluxo = this.initResumoFluxo2();
      } else {
        this.resumoFluxo = this.initResumoFluxo1();
      }
      this.atualizaResumoFluxo();
    }
    return this.resumoFluxo;
  }

  resetResumoFluxo() {
    this.resumoFluxo = this.initResumoFluxo1();
    this.atualizaResumoFluxo();
  }

  private initResumoFluxo1(): any[] {
    if (!this.dadosInscricao) {
      return [];
    }

    let passos = [];
    // agendado é a base para os demais
    passos = [
      {
        id: 1,
        desc: 'Sobre o curso',
        active: false,
        current: true,
        rota: 'curso',
        dadosCurso: true,
        data: [],
      },
      {
        id: 2,
        desc: 'Dados pessoais',
        active: false,
        current: false,
        rota: 'dados-pessoais',
        data: [],
      },
      {
        id: 3,
        desc: 'Pagamento',
        active: false,
        current: false,
        rota: 'pagamento',
        data: [],
      },
      {
        id: 4,
        desc: 'Finalização',
        active: false,
        current: false,
        rota: 'finalizacao',
        hide: true,
        data: [],
      },
    ];

    return passos;
  }

  private initResumoFluxo2(): any[] {
    if (!this.dadosInscricao) {
      return [];
    }

    let passos = [];
    // agendado é a base para os demais
    passos = [
      {
        id: 1,
        desc: 'Sobre o curso',
        active: false,
        current: true,
        rota: 'curso',
        dadosCurso: true,
        data: [],
      },
      {
        id: 2,
        desc: 'Dados pessoais',
        active: false,
        current: false,
        rota: 'dados-pessoais',
        data: [],
      },
      {
        id: 3,
        desc: 'Formas de parcelamento',
        active: false,
        current: false,
        hide: true,
        rota: 'parcelamento',
        final: true,
        data: [],
      },
      {
        id: 4,
        desc: 'Pagamento',
        active: false,
        current: false,
        rota: 'pagamento',
        data: [],
      },
      {
        id: 5,
        desc: 'Finalização',
        active: false,
        current: false,
        rota: 'finalizacao',
        hide: true,
        data: [],
      },
    ];

    return passos;
  }

  private atualizaResumoFluxo() {
    if (!this.resumoFluxo || this.resumoFluxo.length === 0) {
      return;
    }
    if (this.dadosInscricao.turma && this.dadosInscricao.turma.possuiInscricao === false) {
      this.dataFluxo1();
    } else {
      this.dataFluxo2();
    }
    this.resumoFluxoMessage.next(this.resumoFluxo);
  }

  private dataFluxo1 () {
    this.resumoFluxo = this.initResumoFluxo2();

    this.resumoFluxo.forEach(passo => {
      passo.current = this.getPassoAtual() === passo.id;
      if (passo.id < this.getPassoAtual()) {
        passo.active = true;
      }
      switch (passo.id) {
        case 1:
          if (passo.active) {
            passo.data = [
              { label: 'Turno: ', value: this.getTurnoDesc(this.dadosInscricao.turma.turno) },
              { label: 'Campus: ', value: this.dadosInscricao.curso.nomeCampus },
              { label: 'Previsão Início: ', value: `${this.dadosInscricao.turma.data.mes} ${this.dadosInscricao.turma.data.ano}` },
            ];
          }
          break;
        case 2:
          if (passo.active) {
            passo.data = [
              { label: 'Nome: ', value: this.dadosInscricao.candidato.nome },
              { label: 'CPF: ', value: this.cpfCnpjPipe.transform(this.dadosInscricao.candidato.cpf) },
            ];
          }
          break;
        case 4:
          if (passo.active) {
            passo.data = [
              { label: 'Pagamento: ', value: 'Boleto Bancário' },
            ];
          }
          break;
      }
    });
  }

  private dataFluxo2 () {
    this.resumoFluxo.forEach(passo => {
      passo.current = this.getPassoAtual() === passo.id;
      if (passo.id < this.getPassoAtual()) {
        passo.active = true;
      }

      switch (passo.id) {
        case 1:
          if (passo.active) {
            passo.data = [
              { label: 'Turno: ', value: this.getTurnoDesc(this.dadosInscricao.turma.turno) },
              { label: 'Campus: ', value: this.dadosInscricao.curso.nomeCampus },
              { label: 'Previsão Início: ', value: `${this.dadosInscricao.turma.data.mes} ${this.dadosInscricao.turma.data.ano}` },
            ];
          }
          break;

        case 2:
          if (passo.active) {
            passo.data = [
              { label: 'Nome: ', value: this.dadosInscricao.candidato.nome },
              { label: 'CPF: ', value: this.cpfCnpjPipe.transform(this.dadosInscricao.candidato.cpf) },
            ];
          }
          break;

        case 3:
          if (passo.active) {
            passo.data = [
              { label: 'Pagamento: ', value: 'Boleto Bancário' },
            ];
          }
          break;
      }
    });
  }

  preparaCursos(dataCursos) {
    const cursos = [];
    dataCursos.forEach(campus => {
      campus.cursos.forEach(curso => {
        const data = {
          ...curso,
          nome: this.capitalizePipe.transform(curso.nome, this.confCapitalize),
          idCampus: campus.id,
          nomeCampus: this.capitalizePipe.transform(campus.nome, this.confCapitalize),
        };
        cursos.push(data);
      });
    });
    return cursos;
  }

  preparaTurmas(cursos) {
    return cursos.turma.map(turma => {
      const turno = this.getTurnoDesc(turma.turno);
      const data = this.convertDtFromIsoToBr(turma.dataInicio);
      return {
        ...turma,
        data,
        nome: `${turno} / Previsão início ${data.mes} ${data.ano}`,
      };
    });
  }

  preparaParcelamento(turma) {
    if (turma.possuiInscricao) {
      return turma.informativoParcelamento;
    } else {
      return turma.parcelamento.filter(parce => parce.vinculos.includes('TODOS'))[0];
    }
  }

  getTurnoDesc(turno) {
    let retorno = '';
    switch (turno) {
      case 'I':
        retorno = 'Integral';
        break;
      case 'M':
        retorno = 'Matutino';
        break;
      case 'V':
        retorno = 'Vespertino';
        break;
      case 'N':
        retorno = 'Noturno';
        break;
    }

    return retorno;
  }


  private convertDtFromIsoToBr(dateIso: string) {
    if (!dateIso) {
      return null;
    }
    const bits = dateIso.split(' ')[0].split('-');
    const data = `${bits[0]}/${bits[1]}/${bits[2]}`;
    const formated = `${bits[2]}/${bits[1]}/${bits[0]}`;
    const newDate = new Date(data),
      month = newDate.toLocaleString('pt-br', { month: 'long' });
    return {
      data: formated,
      dia: bits[2],
      mes: month,
      ano: bits[0],
    };
  }
}
